import { parseOffset, looksLikeOffsetFile, type MeshOffset } from "./offset";
import type { RangeSource } from "./rangeSource";
import { baseName, dirName, extensionOf, ZipReader, type ZipEntry } from "./zipReader";

/** One OBJ tile plus the sidecars it references. */
export interface MeshTileEntry {
  /** Display name, normally the OBJ file name without its extension. */
  name: string;
  objPath: string;
  mtlPath?: string;
  /** Uncompressed size of the OBJ, used for load ordering and progress. */
  objSize: number;
}

/**
 * A set of OBJ tiles that can be read individually.
 *
 * Backed either by a ZIP read over byte ranges (the usual delivery format, and the only
 * way a 1.8 GB archive is workable in a browser) or by loose files dropped onto the page.
 */
export interface MeshBundle {
  label: string;
  tiles: MeshTileEntry[];
  offset: MeshOffset | null;
  /** Total uncompressed OBJ bytes across all tiles. */
  totalObjBytes: number;
  read(path: string, signal?: AbortSignal): Promise<Uint8Array>;
  readText(path: string, signal?: AbortSignal): Promise<string>;
  /** Reads part of an entry; used to estimate tile extents cheaply. Null when unsupported. */
  readSlice(path: string, offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> | null;
  blob(path: string, mimeType: string, signal?: AbortSignal): Promise<Blob>;
  has(path: string): boolean;
  /** Resolves a path referenced from inside `fromPath` (e.g. a texture named in an MTL). */
  resolve(fromPath: string, reference: string): string | null;
  sizeOf(path: string): number;
}

export async function bundleFromZip(zip: ZipReader, label: string): Promise<MeshBundle> {
  const entries = new Map<string, ZipEntry>();
  for (const entry of zip.list()) entries.set(entry.name, entry);

  const bundle: MeshBundle = {
    label,
    tiles: [],
    offset: null,
    totalObjBytes: 0,
    read: (path, signal) => zip.read(path, signal),
    readText: (path, signal) => zip.readText(path, signal),
    readSlice: (path, offset, length, signal) => {
      const entry = entries.get(path);
      // Only stored entries can be sliced; a deflated archive has to be read whole.
      if (!entry || entry.compressionMethod !== 0) return null;
      return zip.readSlice(entry, offset, length, signal);
    },
    blob: async (path, mimeType, signal) => new Blob([await zip.read(path, signal) as BlobPart], { type: mimeType }),
    has: (path) => entries.has(path),
    resolve: (fromPath, reference) => resolveIn([...entries.keys()], fromPath, reference),
    sizeOf: (path) => entries.get(path)?.uncompressedSize ?? 0,
  };

  await describeBundle(bundle, [...entries.keys()]);
  return bundle;
}

/**
 * A mesh delivered as loose files in a Trimble Connect folder rather than an archive.
 *
 * Each entry is streamed from its own pre-signed download URL, so an individual OBJ tile or
 * texture is fetched only when it is needed, exactly as with the ZIP path.
 */
export async function bundleFromConnectFiles(
  entries: Array<{ name: string; size: number; open: () => RangeSource }>,
  label: string,
): Promise<MeshBundle> {
  const sources = new Map<string, { size: number; source: RangeSource }>();
  for (const entry of entries) {
    sources.set(entry.name, { size: entry.size, source: entry.open() });
  }

  const readAll = async (path: string, signal?: AbortSignal): Promise<Uint8Array> => {
    const entry = sources.get(path);
    if (!entry) throw new Error(`File not found in bundle: ${path}`);
    const size = entry.size || (await entry.source.size());
    return entry.source.read(0, size, signal);
  };

  const bundle: MeshBundle = {
    label,
    tiles: [],
    offset: null,
    totalObjBytes: 0,
    read: readAll,
    readText: async (path, signal) => new TextDecoder().decode(await readAll(path, signal)),
    readSlice: (path, offset, length, signal) => {
      const entry = sources.get(path);
      return entry ? entry.source.read(offset, length, signal) : null;
    },
    blob: async (path, mimeType, signal) =>
      new Blob([(await readAll(path, signal)) as BlobPart], { type: mimeType }),
    has: (path) => sources.has(path),
    resolve: (fromPath, reference) => resolveIn([...sources.keys()], fromPath, reference),
    sizeOf: (path) => sources.get(path)?.size ?? 0,
  };

  await describeBundle(bundle, [...sources.keys()]);
  return bundle;
}

export async function bundleFromFiles(files: File[], label: string): Promise<MeshBundle> {
  const byPath = new Map<string, File>();
  for (const file of files) {
    // Directory drops expose a relative path; plain file pickers only give a name.
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    byPath.set(path, file);
  }

  const bundle: MeshBundle = {
    label,
    tiles: [],
    offset: null,
    totalObjBytes: 0,
    read: async (path) => new Uint8Array(await requireFile(byPath, path).arrayBuffer()),
    readText: async (path) => requireFile(byPath, path).text(),
    readSlice: async (path, offset, length) => {
      const file = requireFile(byPath, path);
      return new Uint8Array(await file.slice(offset, offset + length).arrayBuffer());
    },
    blob: async (path, mimeType) => {
      const file = requireFile(byPath, path);
      return file.type ? file : new Blob([file], { type: mimeType });
    },
    has: (path) => byPath.has(path),
    resolve: (fromPath, reference) => resolveIn([...byPath.keys()], fromPath, reference),
    sizeOf: (path) => byPath.get(path)?.size ?? 0,
  };

  await describeBundle(bundle, [...byPath.keys()]);
  return bundle;
}

/** Finds the OBJ tiles, their MTL sidecars and the shared offset file. */
async function describeBundle(bundle: MeshBundle, paths: string[]): Promise<void> {
  const objPaths = paths.filter((p) => extensionOf(p) === "obj").sort(naturalCompare);

  for (const objPath of objPaths) {
    const stem = objPath.slice(0, objPath.length - 4);
    const mtlPath = paths.find((p) => p === `${stem}.mtl`) ?? paths.find((p) => p === `${stem}.MTL`);
    const objSize = bundle.sizeOf(objPath);
    bundle.tiles.push({
      name: baseName(stem),
      objPath,
      ...(mtlPath ? { mtlPath } : {}),
      objSize,
    });
    bundle.totalObjBytes += objSize;
  }

  const offsetPath = paths.find((p) => looksLikeOffsetFile(baseName(p)));
  if (offsetPath) {
    try {
      bundle.offset = parseOffset(await bundle.readText(offsetPath), baseName(offsetPath));
    } catch (error) {
      console.warn("[mesh] offset file could not be parsed:", error);
    }
  }
}

function requireFile(map: Map<string, File>, path: string): File {
  const file = map.get(path);
  if (!file) throw new Error(`File not found in bundle: ${path}`);
  return file;
}

/**
 * Resolves a reference from inside a file. Exports are inconsistent about whether they
 * write a bare name or a path, so the same-directory candidate is tried first and a
 * name-only match anywhere in the bundle is the fallback.
 */
function resolveIn(paths: string[], fromPath: string, reference: string): string | null {
  const normalised = reference.replace(/\\/g, "/").replace(/^\.\//, "");
  const sameDirectory = dirName(fromPath) + baseName(normalised);
  if (paths.includes(sameDirectory)) return sameDirectory;
  if (paths.includes(normalised)) return normalised;
  const target = baseName(normalised).toLowerCase();
  return paths.find((p) => baseName(p).toLowerCase() === target) ?? null;
}

/** Orders `mesh_2` before `mesh_10`, which plain string sorting gets wrong. */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}
